import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Platform, Text, View } from "react-native"
import type { ViewStyle } from "react-native"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import { runOnJS } from "react-native-reanimated"
import RNSvg, { Circle, Defs, LinearGradient, Line, Path, Stop } from "react-native-svg"
import { numberFormat } from "@ecountabl/lib"
import { textStyles } from "../../style"
import { ThemeContext } from "../../utility/context/ThemeContext"
import { EnvironmentalImpactChartTooltip } from "./environmental-impact-chart-tooltip"

const ENV_CHART_PADDING = { left: 12, right: 12, top: 12, bottom: 18 } as const

type EnvironmentalImpactChartProps = {
  data: {
    CumulativeSum: number
    Label: string
    MonthNum: string
    YearNum: string
  }[]
}

export const EnvironmentalImpactChart = ({
  data,
}: EnvironmentalImpactChartProps): JSX.Element => {
  const theme = useContext(ThemeContext).theme
  const { primary, textMain, bgMain, borderColor } = theme.colors

  const [width, setWidth] = useState(0)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<React.ElementRef<typeof View> | null>(null)

  const points = useMemo(() => {
    const sorted = (data || []).slice().sort((a, b) => {
      const ay = Number(a?.YearNum || 0)
      const by = Number(b?.YearNum || 0)
      if (ay !== by) return ay - by
      const am = Number(a?.MonthNum || 0)
      const bm = Number(b?.MonthNum || 0)
      return am - bm
    })
    return sorted.map((p) => ({
      label: String(p?.Label || ""),
      value: Math.max(0, Number(p?.CumulativeSum || 0)),
    }))
  }, [data])

  useEffect(() => {
    if (points.length === 0) {
      if (focusedIndex !== -1) setFocusedIndex(-1)
      return
    }
    if (focusedIndex < 0 || focusedIndex >= points.length) {
      setFocusedIndex(points.length - 1)
    }
  }, [focusedIndex, points.length])

  const chartHeight = 180
  const padding = ENV_CHART_PADDING
  const plotW = Math.max(1, width - padding.left - padding.right)
  const plotH = Math.max(1, chartHeight - padding.top - padding.bottom)

  const maxY = useMemo(() => {
    const m = Math.max(1, ...points.map((p) => p.value))
    return m * 1.05
  }, [points])

  const coords = useMemo(() => {
    if (points.length === 0 || width <= 0) return []
    const n = points.length
    return points.map((p, i) => {
      const x = n <= 1 ? padding.left : (padding.left + ((i / (n - 1)) * plotW))
      const y = padding.top + plotH - (((p.value || 0) / maxY) * plotH)
      return { x, y }
    })
  }, [maxY, padding.left, padding.top, plotH, plotW, points, width])

  const { linePath, areaPath } = useMemo(() => {
    if (coords.length === 0) return { linePath: "", areaPath: "" }
    const line = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ")
    const baseY = padding.top + plotH
    const area = `${line} L ${coords[coords.length - 1].x} ${baseY} L ${coords[0].x} ${baseY} Z`
    return { linePath: line, areaPath: area }
  }, [coords, padding.top, plotH])

  const focus = useMemo(() => {
    if (focusedIndex < 0 || focusedIndex >= points.length) return null
    const c = coords[focusedIndex]
    if (!c) return null
    return { x: c.x, y: c.y, point: points[focusedIndex] }
  }, [coords, focusedIndex, points])

  const toRgba = (color: string, alpha: number): string => {
    const c = String(color || "").trim()
    if (!c) return `rgba(37,99,235,${alpha})`
    if (c.startsWith("rgba(")) {
      const rgbaMatch = c.match(/^rgba\(\s*([0-9]+\s*,\s*[0-9]+\s*,\s*[0-9]+)(?:\s*,\s*[\d.]+)?\s*\)$/)
      if (rgbaMatch?.[1]) return `rgba(${rgbaMatch[1]},${alpha})`
      return `rgba(37,99,235,${alpha})`
    }
    if (c.startsWith("rgb(")) {
      const inner = c.replace("rgb(", "").replace(")", "")
      return `rgba(${inner},${alpha})`
    }
    let hex = c.startsWith("#") ? c.slice(1) : c
    if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("")
    if (hex.length !== 6) return `rgba(37,99,235,${alpha})`
    const n = parseInt(hex, 16)
    const r = (n >> 16) & 255
    const g = (n >> 8) & 255
    const b = n & 255
    return `rgba(${r},${g},${b},${alpha})`
  }

  const handleFocusAtX = useCallback((x: number) => {
    if (points.length === 0) return
    const clampedX = Math.max(padding.left, Math.min(width - padding.right, x))
    const t = plotW <= 1 ? 0 : (clampedX - padding.left) / plotW
    const idx = Math.round(t * (points.length - 1))
    setFocusedIndex(Math.max(0, Math.min(points.length - 1, idx)))
  }, [padding.left, padding.right, plotW, points.length, width])

  const handleFocusFromClientX = (clientX: number) => {
    if (Platform.OS !== "web") return
    const node = containerRef.current as unknown as
      | { getBoundingClientRect?: () => { left: number } }
      | null
    const rect = node?.getBoundingClientRect?.()
    if (!rect) return
    handleFocusAtX(clientX - rect.left)
  }

  const panGesture = useMemo(() => {
    if (Platform.OS === "web") return null
    return Gesture.Pan()
      .minDistance(0)
      .activeOffsetX([-1, 1])
      .activeOffsetY([-1, 1])
      .onBegin((e) => {
        runOnJS(handleFocusAtX)(e.x)
      })
      .onUpdate((e) => {
        runOnJS(handleFocusAtX)(e.x)
      })
  }, [handleFocusAtX])

  const webHandlers = Platform.OS === "web"
    ? {
        onMouseMove: (e: unknown): void => {
          const ne = (e as { nativeEvent?: { clientX?: number } })?.nativeEvent
          const clientX = ne?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
        },
        onPointerDown: (e: unknown): void => {
          const ne =
            (e as {
              nativeEvent?: { clientX?: number; pointerId?: number; target?: unknown }
            })?.nativeEvent
          if (typeof ne?.clientX === "number") handleFocusFromClientX(ne.clientX)
          const t = ne?.target as { setPointerCapture?: (id: number) => void } | undefined
          if (typeof ne?.pointerId === "number") t?.setPointerCapture?.(ne.pointerId)
        },
        onPointerMove: (e: unknown): void => {
          const ne = (e as { nativeEvent?: { clientX?: number } })?.nativeEvent
          const clientX = ne?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
        },
        onTouchStart: (e: unknown): void => {
          const ne = (
            e as { nativeEvent?: { touches?: Array<{ clientX?: number }> } }
          )?.nativeEvent
          const clientX = ne?.touches?.[0]?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
          ;(e as { preventDefault?: () => void })?.preventDefault?.()
        },
        onTouchMove: (e: unknown): void => {
          const ne = (
            e as { nativeEvent?: { touches?: Array<{ clientX?: number }> } }
          )?.nativeEvent
          const clientX = ne?.touches?.[0]?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
          ;(e as { preventDefault?: () => void })?.preventDefault?.()
        },
      }
    : {}

  const chartPlot = (
    <View
      style={[
        { position: "relative", width: "100%", height: chartHeight, marginTop: 6 },
        Platform.OS === "web"
          ? ({
              touchAction: "none",
              userSelect: "none",
              cursor: "crosshair",
            } as ViewStyle & {
              touchAction?: string
              userSelect?: string
              cursor?: string
            })
          : undefined,
      ]}
      pointerEvents="box-only"
      ref={containerRef}
      onLayout={(e) => {
        const w = e.nativeEvent.layout.width
        if (w && w !== width) setWidth(w)
      }}
      onStartShouldSetResponderCapture={() => true}
      onMoveShouldSetResponderCapture={() => true}
      onStartShouldSetResponder={() => true}
      onMoveShouldSetResponder={() => true}
      onResponderTerminationRequest={() => false}
      onResponderGrant={(e) => {
        handleFocusAtX(e.nativeEvent.locationX)
      }}
      onResponderMove={(e) => {
        handleFocusAtX(e.nativeEvent.locationX)
      }}
      {...webHandlers}
    >
      <RNSvg width={width || 1} height={chartHeight}>
        <Defs>
          <LinearGradient
            id="envArea"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1={padding.top}
            x2="0"
            y2={padding.top + plotH}
          >
            <Stop offset="0" stopColor={primary} stopOpacity={0.28} />
            <Stop offset="1" stopColor={primary} stopOpacity={0} />
          </LinearGradient>
        </Defs>

        <Line
          x1={padding.left}
          x2={(width || 1) - padding.right}
          y1={padding.top + plotH}
          y2={padding.top + plotH}
          stroke={toRgba(borderColor, 0.6)}
          strokeWidth={1}
        />

        {areaPath ? <Path d={areaPath} fill="url(#envArea)" /> : null}
        {linePath ? <Path d={linePath} fill="none" stroke={primary} strokeWidth={2} /> : null}

        {focus ? (
          <>
            <Line
              x1={focus.x}
              x2={focus.x}
              y1={padding.top}
              y2={padding.top + plotH}
              stroke={toRgba(primary, 0.5)}
              strokeWidth={1}
            />
            <Circle cx={focus.x} cy={focus.y} r={4} fill={primary} />
            <Circle cx={focus.x} cy={focus.y} r={2} fill={bgMain} />
          </>
        ) : null}
      </RNSvg>

      {focus ? (
        <EnvironmentalImpactChartTooltip
          label={focus.point.label}
          valueText={`${numberFormat(Math.round(focus.point.value), { shortenLargeNumbers: true })} kW`}
          focusX={focus.x}
          focusY={focus.y}
          chartWidth={width || 1}
          chartHeight={chartHeight}
          bgMain={bgMain}
          borderColor={borderColor}
          textMain={textMain}
        />
      ) : null}
    </View>
  )

  return (
    <View style={{ paddingHorizontal: 10, overflow: "hidden", marginBottom: 16 }}>
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10 }}>
        <Text style={[textStyles.h4, { color: primary }]}>
          kW of Solar Financed Since Inception
        </Text>
      </View>
      {panGesture ? <GestureDetector gesture={panGesture}>{chartPlot}</GestureDetector> : chartPlot}
      <Text style={[textStyles.subscript, { color: theme.colors.textSecond, marginTop: 6 }]}>
        {Platform.OS === "web" ? "Hover" : "Drag"} to explore history
      </Text>
    </View>
  )
}
